// M3 field definitions (Postwar & Offshore, 1946-1999). Same FieldDef shape as src/data/fields.js and
// src/data/m2/fields.js, plus the M3 keys below. Attached to FIELDS by src/data/fields.js as NON-ENUMERABLE
// properties until enableM3Fields() is called (docs/M3_SPEC.md §0), exactly like M2.
//
// Depths: for offshore fields, stratum and reservoir depths are feet BELOW THE MUDLINE (seabed). Total measured depth
// from the rig floor = plot water depth + depth below mudline (rig maxDepth must cover both; M3_SPEC §1).
//
// M3 FieldDef extensions (all optional; the M1/M2 sim ignores them):
//   waterDepthFt      water at the shallow edge of the grid (0 = land); existing key, read by the renderers
//   waterDepthMaxFt   water at the deep edge; plots interpolate linearly along waterAxis (plotWaterDepth below)
//   waterAxis         'x' (default) | 'y'
//   environment       { kind, stormSeason?, winterStorms?, ice?, fog?, currentKn?, subsidence?, shallowGas?, heatC?, coldC? }
//                       stormSeason/winterStorms: { months: [1-12], peakMonth?, perYear (storms reaching the field),
//                       hsFt: [typical, severe] (significant wave height), named? (hurricanes get names and
//                       evacuations) }; ice: { kind: 'permafrost'|'sea_ice'|'bog', months, drillSeason (months a
//                       non-arctic rig can work on ice roads/pads) }
//   shoreBase / distanceToShoreMi   logistics base and mean distance (helicopter/boat time, M3_SPEC §4)
//   concession        { kind: 'lease'|'concession'|'licence'|'production_sharing'|'jv'|'service_only', bidRound?: roundId,
//                       stateCompany?, workCommitmentWells?, note } (M3_SPEC §6)
//   serviceOnly       { operator, from, until? } the state company owns the field: you can only charter rigs to it
//                       (contract drilling at day rate), never lease (Mexico/PEMEX: Cantarell)
//   discoveredYear    for fields found before 1946 (Burgan 1938, Abqaiq 1940, Agha Jari 1938): history only
//   firstExportDay    first cargo/pipeline day if later than opensDay (informational)
//   outletNote        why the field has no transport for a while (Prudhoe Bay before TAPS)
// Strata extensions: geopressured (impermeable overpressured shale: high pore, no kicks, MW below pore − 1 sloughs),
//   permafrost (frozen ground: thaw subsidence under a warm rig/wellhead, needs insulated conductor),
//   shallowFlow (deepwater shallow-water-flow sand), plus the M2 keys (gas, water, heaving, salt, lossZone, balling)
// Reservoir extensions: eorPotential { waterflood?, gas?, steam?, co2? } (rfAdd each method can unlock, M3_SPEC §11),
//   startRemainingFrac (field already produced by others before you get access: Samotlor), startWaterCut,
//   waterDrive / gasCap (M2), compacting (chalk subsidence), heavy (API < 22: needs PCP/steam), viscosityCp
//
// Fit rule (tests): a reservoir's host stratum must contain top + dip*min(maxRadius,4) + thick; host strata stay at
// <= 8.8 ppg pore; non-reservoir strata above the pay stay <= 8.8 unless gas/water/geopressured.
import { dateToDay } from '../../core/time.js';

const FRAC_CAP_PPG = 19; // same cap as src/data/fields.js
function stack(layers) {
  let top = 0;
  return layers.map(([bottom, rock, name, hard, pore, stableDays, extra = {}]) => {
    const mid = (top + bottom) / 2;
    const s = { top, bottom, rock, name, hard, pore, frac: +Math.min(FRAC_CAP_PPG, 12 + 1.6 * mid / 1000 + (hard >= 5 ? 0.6 : 0)).toFixed(2), stableDays, h2s: 0, ...extra };
    top = bottom;
    return s;
  });
}

const RES = { di: 0.12, b: 0.5, api: 35, sour: false, rf: 0.25, h2s: 0 };
const OFF = { di: 0.2, b: 0.8, api: 36, sour: false, rf: 0.3, h2s: 0 }; // DESIGN §5.5: offshore b/Di 0.8/0.2
const D = (y, m, d) => dateToDay(y, m, d);
const WATER = { water: true };
const GEO = (pore) => ({ geopressured: true, porePpgNote: pore });

// ------------------------------------------------------------------ environment presets (estimates)
// Gulf of Mexico: ~0.6 tropical systems a year reach a given shelf block area; Camille (1969) and Andrew (1992)
// produced 40-ft+ seas. Winter cold fronts ("northers") every few weeks.
const GULF_ENV = { kind: 'shelf',
  stormSeason: { months: [6, 7, 8, 9, 10, 11], peakMonth: 9, perYear: 0.6, hsFt: [14, 42], named: true },
  winterStorms: { months: [12, 1, 2, 3], perYear: 3, hsFt: [7, 13] }, fog: { months: [12, 1, 2], daysPerMonth: 4 } };
// North Sea: winter gales October-March; northern North Sea 100-year Hs ~ 15 m (49 ft), max wave ~ 30 m.
const NORTH_SEA_ENV = { kind: 'harsh',
  winterStorms: { months: [10, 11, 12, 1, 2, 3], perYear: 8, hsFt: [18, 50] },
  stormSeason: { months: [4, 5, 9], perYear: 2, hsFt: [12, 25] }, fog: { months: [5, 6, 7], daysPerMonth: 3 } };
// Persian Gulf: the summer shamal (NW wind) and winter shamals; short steep seas, 6-12 ft.
const ARABIAN_GULF_ENV = { kind: 'shelf', heatC: 48,
  stormSeason: { months: [5, 6, 7], peakMonth: 6, perYear: 2, hsFt: [6, 12], named: false },
  winterStorms: { months: [12, 1, 2], perYear: 2, hsFt: [5, 10] } };

// ------------------------------------------------------------------ field definitions
export const M3_FIELD_DEFS = [
  // ======================================================================== Abqaiq, Saudi Arabia (land)
  {
    id: 'abqaiq', name: 'Abqaiq, al-Hasa', body: 'earth', region: 'saudi', regionId: 'saudi',
    era: 'postwar', opensDay: D(1946, 1, 1), discoveredYear: 1940, grid: { w: 6, h: 4 }, waterDepthFt: 0,
    lat: 25.94, lon: 49.68, startPlot: 9, recommendedRig: 'rotary',
    leaseBase: 40000, leaseYear: 1946, royalty: 0.12,
    concession: { kind: 'concession', stateCompany: 'Aramco (Socal/Texaco, later + Exxon/Mobil)', note: 'You hold a sub-concession inside the Aramco area: 12% royalty until the 1950 fifty-fifty.', minReputation: 50, signatureBonus: 1e6, bonusYear: 1946 },
    boomCurve: [[1946, 1], [1948.5, 2], [1951, 2.5], [1960, 2], [1973, 1.5]],
    environment: { kind: 'desert', heatC: 49, stormSeason: { months: [4, 5, 6], perYear: 3, hsFt: [0, 0], named: false, sandstorm: true } },
    transport: ['gathering_pipe@1946', 'coastal_tanker@1946', 'tanker@1950', 'tapline@1950', 'supertanker_charter@1959', 'vlcc_charter@1966', 'ulcc_charter@1976'],
    blurb: 'Discovered in 1940 and shut in by the war. Now the pipelines to Ras Tanura are going in, and the Arab Zone flows on its own.',
    strata: stack([
      [60,    'sand',      'Dune sand',                         1, 8.5, 3],
      [900,   'limestone', 'Dammam & Rus limestone',            5, 8.6, 150],
      [1200,  'limestone', 'Umm er Radhuma aquifer',            5, 8.9, 150, WATER],
      [3000,  'shale',     'Aruma & Wasia shale',               3, 8.8, 40],
      [4300,  'sand',      'Biyadh sands',                      4, 8.8, 25],
      [5600,  'limestone', 'Thamama & Buwaib limestone',        6, 8.8, 200],
      [7300,  'limestone', 'Arab Zone limestone',               6, 8.8, 200],
      [9000,  'limestone', 'Hanifa & Tuwaiq Mountain',          6, 8.9, 200],
      [14000, 'dolomite',  'Khuff dolomite (sour gas)',         7, 9.8, 200, { gas: true, h2s: 5000 }],
    ]),
    prospects: [
      { cx: 2.5, cy: 1.5, rx: 2.4, ry: 1.4, reservoirs: [
        { id: 'aq_arab_d', name: 'Arab-D (Abqaiq)', top: 6100, dipFtPerPlot: 40, thick: 250, ooip: 1.2e9, qiRange: [2000, 12000], pore: 9.4, gusherChance: 0.1,
          ...RES, rf: 0.35, di: 0.06, api: 36, sour: true, h2s: 150, waterDrive: true, eorPotential: { waterflood: 0.1, gas: 0.05 } },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0001, fire: 0.0002, heat: 0.0006 },
  },

  // ======================================================================== Ghawar, Saudi Arabia
  {
    id: 'ghawar', name: 'Ghawar (Ain Dar to Hawiyah)', body: 'earth', region: 'saudi', regionId: 'saudi',
    era: 'postwar', opensDay: D(1948, 3, 1), discoveryDay: D(1948, 7, 1), grid: { w: 10, h: 4 }, waterDepthFt: 0,
    lat: 25.4, lon: 49.6, startPlot: 11, recommendedRig: 'rotary',
    leaseBase: 50000, leaseYear: 1948, royalty: 0.12,
    concession: { kind: 'concession', stateCompany: 'Aramco', note: 'A 170-mile anticline. Ain Dar No. 1 (1948) proves the north end; Haradh at the south end (1949) shows it is one field.', minReputation: 55, signatureBonus: 1.5e6, bonusYear: 1948 },
    boomCurve: [[1948.2, 1], [1948.6, 4], [1951, 5], [1956, 4], [1973, 3], [1980, 0.5]],
    environment: { kind: 'desert', heatC: 50, stormSeason: { months: [4, 5, 6], perYear: 3, hsFt: [0, 0], named: false, sandstorm: true } },
    transport: ['gathering_pipe@1948', 'coastal_tanker@1948', 'tanker@1950', 'tapline@1950', 'supertanker_charter@1959', 'vlcc_charter@1966', 'ulcc_charter@1976'],
    blurb: 'Rumours from the Aramco camp at Dhahran: a structure longer than the state of Delaware. If it holds oil end to end, it is the largest field on Earth.',
    strata: stack([
      [80,    'sand',      'Dune sand & gravel',                1, 8.5, 3],
      [1000,  'limestone', 'Dammam & Rus limestone',            5, 8.6, 150],
      [1300,  'limestone', 'Umm er Radhuma aquifer',            5, 8.9, 150, WATER],
      [3200,  'shale',     'Aruma & Wasia shale',               3, 8.8, 40],
      [4600,  'sand',      'Biyadh & Wasia sands',              4, 8.8, 25],
      [6200,  'limestone', 'Thamama, Buwaib & Sulaiy',          6, 8.8, 200],
      [7800,  'limestone', 'Arab Zone limestone',               6, 8.8, 200],
      [10000, 'limestone', 'Hanifa & Tuwaiq Mountain',          6, 8.9, 200],
      [15000, 'dolomite',  'Khuff dolomite (sour gas)',         7, 9.8, 200, { gas: true, h2s: 6000 }],
    ]),
    prospects: [
      { cx: 1.0, cy: 1.5, rx: 1.5, ry: 1.4, reservoirs: [
        { id: 'gh_ain_dar', name: 'Arab-D (Ain Dar)', top: 6300, dipFtPerPlot: 35, thick: 280, ooip: 2.5e9, qiRange: [3000, 15000], pore: 9.5, gusherChance: 0.1,
          ...RES, rf: 0.35, di: 0.05, api: 34, sour: true, h2s: 200, waterDrive: true, eorPotential: { waterflood: 0.12 } } ] },
      { cx: 3.5, cy: 1.5, rx: 1.5, ry: 1.4, reservoirs: [
        { id: 'gh_shedgum', name: 'Arab-D (Shedgum)', top: 6350, dipFtPerPlot: 35, thick: 280, ooip: 2.5e9, qiRange: [3000, 15000], pore: 9.5, gusherChance: 0.1,
          ...RES, rf: 0.35, di: 0.05, api: 34, sour: true, h2s: 200, waterDrive: true, eorPotential: { waterflood: 0.12 } } ] },
      { cx: 6.0, cy: 1.5, rx: 1.5, ry: 1.4, reservoirs: [
        { id: 'gh_uthmaniyah', name: 'Arab-D (Uthmaniyah)', top: 6400, dipFtPerPlot: 35, thick: 270, ooip: 2.5e9, qiRange: [3000, 14000], pore: 9.5, gusherChance: 0.1,
          ...RES, rf: 0.35, di: 0.05, api: 33, sour: true, h2s: 250, waterDrive: true, eorPotential: { waterflood: 0.12 } } ] },
      { cx: 8.5, cy: 1.5, rx: 1.5, ry: 1.4, reservoirs: [
        { id: 'gh_hawiyah', name: 'Arab-D (Hawiyah–Haradh)', top: 6500, dipFtPerPlot: 35, thick: 250, ooip: 1.8e9, qiRange: [2000, 10000], pore: 9.5, gusherChance: 0.08,
          ...RES, rf: 0.33, di: 0.06, api: 32, sour: true, h2s: 300, waterDrive: true, eorPotential: { waterflood: 0.12 } } ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0001, fire: 0.0002, heat: 0.0007 },
  },

  // ======================================================================== Safaniya, Saudi Arabia (offshore Gulf)
  {
    id: 'safaniya', name: 'Safaniya (offshore Arabian Gulf)', body: 'earth', region: 'saudi', regionId: 'saudi',
    era: 'postwar', opensDay: D(1951, 1, 1), discoveryDay: D(1951, 4, 1), grid: { w: 8, h: 4 },
    waterDepthFt: 30, waterDepthMaxFt: 110, waterAxis: 'x', shoreBase: 'Ras Tanura', distanceToShoreMi: 15,
    lat: 28.0, lon: 48.85, startPlot: 10, recommendedRig: 'rotary',
    leaseBase: 80000, leaseYear: 1951, royalty: 0.12,
    concession: { kind: 'concession', stateCompany: 'Aramco', note: 'The largest offshore field in the world, in barely 100 ft of water. Heavy, sour, enormous.', minReputation: 55, signatureBonus: 2e6, bonusYear: 1951 },
    boomCurve: [[1951, 1], [1951.5, 3], [1957, 3], [1973, 2]],
    environment: ARABIAN_GULF_ENV,
    transport: ['coastal_tanker@1951', 'tanker@1951', 'offshore_pipeline@1957', 'supertanker_charter@1959', 'vlcc_charter@1966', 'ulcc_charter@1976'],
    blurb: 'Aramco’s seismic boats found a giant just off the coast north of Ras Tanura. Shallow, warm water, a summer shamal and very heavy crude.',
    strata: stack([
      [40,    'sand',      'Seabed sand & silt',                1, 8.5, 3],
      [700,   'limestone', 'Dammam limestone',                  5, 8.6, 150],
      [900,   'limestone', 'Umm er Radhuma aquifer',            5, 8.9, 150, WATER],
      [3800,  'shale',     'Aruma & Wasia shale',               3, 8.8, 40],
      [4600,  'sand',      'Khafji sand member',                4, 8.8, 25],
      [4700,  'shale',     'Shale parting',                     3, 8.8, 40],
      [6000,  'sand',      'Safaniya sand member',              4, 8.8, 25],
      [12000, 'limestone', 'Shuaiba & deeper carbonates',       6, 9.0, 200],
    ]),
    prospects: [
      { cx: 3.5, cy: 1.5, rx: 3.2, ry: 1.6, reservoirs: [
        { id: 'sa_khafji', name: 'Khafji sand', top: 3900, dipFtPerPlot: 20, thick: 120, ooip: 6e8, qiRange: [800, 6000], pore: 9.3, gusherChance: 0.05,
          ...OFF, rf: 0.3, di: 0.08, api: 28, sour: true, h2s: 200, heavy: false },
        { id: 'sa_safaniya', name: 'Safaniya sand', top: 4750, dipFtPerPlot: 30, thick: 180, ooip: 3e9, qiRange: [1500, 10000], pore: 9.4, gusherChance: 0.05,
          ...OFF, rf: 0.3, di: 0.06, api: 27, sour: true, h2s: 300, waterDrive: true, eorPotential: { waterflood: 0.1 } },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0001, fire: 0.0003, heat: 0.0005, storm: 0.0004 },
  },

  // ======================================================================== Burgan, Kuwait
  {
    id: 'burgan', name: 'Greater Burgan, Kuwait', body: 'earth', region: 'kuwait', regionId: 'kuwait',
    era: 'postwar', opensDay: D(1946, 1, 1), discoveredYear: 1938, firstExportDay: D(1946, 6, 30), grid: { w: 10, h: 5 }, waterDepthFt: 0,
    lat: 28.95, lon: 47.95, startPlot: 23, recommendedRig: 'rotary',
    leaseBase: 40000, leaseYear: 1946, royalty: 0.12,
    concession: { kind: 'concession', stateCompany: 'Kuwait Oil Company (Anglo-Iranian & Gulf)', note: 'The 1934 KOC concession; fifty-fifty from Dec 1951, 60% state participation 1974, 100% in 1975.', minReputation: 50, signatureBonus: 7.5e5, bonusYear: 1946 },
    boomCurve: [[1946, 1], [1946.5, 3], [1952, 3], [1975, 0.5]],
    environment: { kind: 'desert', heatC: 51, stormSeason: { months: [5, 6, 7], perYear: 3, hsFt: [0, 0], named: false, sandstorm: true } },
    transport: ['gathering_pipe@1946', 'coastal_tanker@1946', 'tanker@1950', 'supertanker_charter@1959', 'vlcc_charter@1966', 'ulcc_charter@1976'],
    blurb: 'Burgan No. 1 found the sand in 1938; the war shut the wells in with cement. On 30 June 1946 the Emir turns a silver valve at Mina al-Ahmadi.',
    strata: stack([
      [50,    'sand',      'Desert sand & gatch',               1, 8.5, 3],
      [1100,  'limestone', 'Kuwait Group & Dammam',             5, 8.6, 150],
      [1300,  'limestone', 'Rus & Radhuma aquifer',             5, 8.9, 150, WATER],
      [3000,  'shale',     'Aruma & Mutriba shale',             3, 8.8, 40],
      [3500,  'limestone', 'Mauddud limestone',                 5, 8.8, 150],
      [5200,  'sand',      'Burgan sandstone',                  4, 8.8, 25],
      [6500,  'limestone', 'Shuaiba limestone',                 6, 8.8, 200],
      [12000, 'limestone', 'Minagish & deeper',                 6, 9.2, 200],
    ]),
    prospects: [
      { cx: 3.5, cy: 2, rx: 3.3, ry: 2.2, reservoirs: [
        { id: 'bg_mauddud', name: 'Mauddud limestone', top: 3050, dipFtPerPlot: 20, thick: 120, ooip: 4e8, qiRange: [500, 4000], pore: 9.4, gusherChance: 0.1, ...RES, rf: 0.25, di: 0.08, api: 31, sour: true, h2s: 80 },
        { id: 'bg_burgan', name: 'Burgan sand', top: 3700, dipFtPerPlot: 30, thick: 400, ooip: 4e9, qiRange: [3000, 20000], pore: 9.8, gusherChance: 0.25,
          ...RES, rf: 0.38, di: 0.05, api: 32, sour: true, h2s: 50, waterDrive: true },
      ] },
      { cx: 8.5, cy: 2, rx: 1.2, ry: 1.6, reservoirs: [
        { id: 'bg_magwa', name: 'Burgan sand (Magwa–Ahmadi)', top: 3600, dipFtPerPlot: 30, thick: 300, ooip: 1.2e9, qiRange: [2000, 12000], pore: 9.7, gusherChance: 0.2,
          ...RES, rf: 0.35, di: 0.06, api: 33, sour: true, h2s: 50, waterDrive: true },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0001, fire: 0.0003, heat: 0.0007 },
  },

  // ======================================================================== Agha Jari, Iran
  {
    id: 'agha_jari', name: 'Agha Jari, Khuzestan', body: 'earth', region: 'persia', regionId: 'persia',
    era: 'postwar', opensDay: D(1946, 1, 1), discoveredYear: 1938, grid: { w: 8, h: 4 }, waterDepthFt: 0,
    lat: 30.75, lon: 49.83, startPlot: 11, recommendedRig: 'rotary',
    leaseBase: 30000, leaseYear: 1946, royalty: 0.2,
    concession: { kind: 'concession', stateCompany: 'Anglo-Iranian Oil Co. → NIOC (1951) → Consortium (1954)', note: 'Nationalized 1 May 1951; reopened to a Western consortium in Oct 1954 (US independents bought 5% through Iricon in 1955); nationalized again 1979.', minReputation: 45, signatureBonus: 5e5, bonusYear: 1946 },
    boomCurve: [[1946, 1], [1950, 1.5], [1951.3, 0.2], [1954.8, 1.2], [1960, 1.5], [1978.9, 0.2]],
    environment: { kind: 'desert', heatC: 50 },
    transport: ['trunk_pipeline@1946', 'coastal_tanker@1946', 'tanker@1950', 'supertanker_charter@1959', 'vlcc_charter@1966', 'ulcc_charter@1976'],
    blurb: 'A long Zagros anticline feeding the Abadan refinery, the largest in the world. Very prolific wells; very fragile politics.',
    strata: stack([
      [3000,  'sandstone', 'Agha Jari sandstone & marl',        3, 8.6, 40],
      [4000,  'marl',      'Mishan marl',                       3, 8.7, 40],
      [6200,  'anhydrite', 'Gachsaran evaporites (cap rock)',   4, 8.8, 60, { salt: true, h2s: 200 }],
      [8000,  'limestone', 'Asmari limestone (fractured)',      6, 8.8, 200, { lossZone: true, frac: 12.6, h2s: 500 }],
      [9500,  'shale',     'Pabdeh & Gurpi shale',              4, 9.0, 60],
      [14000, 'limestone', 'Bangestan (Sarvak) limestone',      6, 9.6, 200],
    ]),
    prospects: [
      { cx: 3.5, cy: 1.5, rx: 3.5, ry: 1.2, reservoirs: [
        { id: 'aj_asmari', name: 'Asmari limestone', top: 6300, dipFtPerPlot: 50, thick: 900, ooip: 3e9, qiRange: [5000, 30000], pore: 10.2, gusherChance: 0.4,
          ...RES, rf: 0.3, di: 0.06, api: 34, sour: true, h2s: 500, gasCap: true, eorPotential: { gas: 0.08 } },
      ] },
    ],
    seeps: [10, 13],
    hazards: { lightning: 0.0001, fire: 0.0005, heat: 0.0006 },
  },

  // ======================================================================== Leduc, Alberta
  {
    id: 'leduc', name: 'Leduc–Woodbend, Alberta', body: 'earth', region: 'alberta', regionId: 'alberta',
    era: 'postwar', opensDay: D(1946, 11, 20), discoveryDay: D(1947, 2, 13), grid: { w: 8, h: 4 }, waterDepthFt: 0,
    lat: 53.33, lon: -113.73, startPlot: 10, prorationGroup: 'alberta', recommendedRig: 'rotary',
    leaseBase: 15000, leaseYear: 1947, royalty: 0.125,
    concession: { kind: 'lease', note: 'Freehold and Crown leases; Alberta Crown sales by sealed bid from 1947. Conservation Board prorates from 1950.' },
    boomCurve: [[1946.9, 1], [1947.15, 6], [1947.5, 8], [1949, 4], [1953, 2], [1960, 1]],
    environment: { kind: 'steppe', coldC: -35, winterStorms: { months: [12, 1, 2], perYear: 3, hsFt: [0, 0], blizzard: true } },
    transport: ['tank_truck@1947', 'rail@1947', 'gathering_pipe@1948', 'trunk_pipeline@1950'],
    blurb: 'Imperial Oil has drilled 133 dry holes in western Canada. Vern Hunter is spudding No. 1 on Mike Turta’s farm south of Edmonton, aiming for a Devonian reef nobody has seen.',
    strata: stack([
      [80,    'gravel',    'Glacial till',                      1, 8.5, 3],
      [1800,  'shale',     'Edmonton & Belly River shale',      3, 8.6, 50],
      [2000,  'sand',      'Viking sand (gas)',                 4, 9.0, 25, { gas: true }],
      [3800,  'shale',     'Colorado shale',                    3, 8.7, 50],
      [4200,  'sand',      'Mannville sands',                   4, 8.7, 30],
      [4700,  'limestone', 'Wabamun & Graminia',                6, 8.7, 200],
      [4950,  'dolomite',  'Nisku (D-2) dolomite',              6, 8.7, 200],
      [5040,  'shale',     'Ireton shale',                      3, 8.8, 40],
      [6000,  'limestone', 'Leduc reef (D-3)',                  6, 8.8, 200],
      [8000,  'limestone', 'Cooking Lake & Beaverhill Lake',    6, 9.0, 200],
    ]),
    prospects: [
      { cx: 2, cy: 1.5, rx: 1.8, ry: 1.3, reservoirs: [
        { id: 'le_nisku', name: 'Nisku D-2', top: 4750, dipFtPerPlot: 20, thick: 60, ooip: 4e7, qiRange: [100, 800], pore: 9.0, gusherChance: 0.02, ...RES, rf: 0.25, di: 0.15, api: 39 },
        { id: 'le_d3', name: 'Leduc D-3 reef', top: 5066, dipFtPerPlot: 25, thick: 300, ooip: 3e8, qiRange: [300, 2500], pore: 9.2, gusherChance: 0.05,
          ...RES, rf: 0.35, di: 0.1, api: 39, waterDrive: true, gasCap: true },
      ] },
      { cx: 6, cy: 2, rx: 1.4, ry: 1.2, reservoirs: [
        { id: 'le_d3_woodbend', name: 'D-3 reef (Woodbend)', top: 5150, dipFtPerPlot: 25, thick: 350, ooip: 2e8, qiRange: [300, 2000], pore: 9.2, gusherChance: 0.04,
          ...RES, rf: 0.35, di: 0.1, api: 39, waterDrive: true },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0002, fire: 0.0003, cold: 0.0006 },
  },

  // ======================================================================== Gulf of Mexico shelf (Ship Shoal & beyond)
  {
    id: 'gulf_shelf', name: 'Gulf of Mexico Shelf (Ship Shoal–Eugene Island)', body: 'earth', region: 'gulf_of_mexico', regionId: 'gulf_of_mexico',
    era: 'postwar', opensDay: D(1947, 9, 1), discoveryDay: D(1947, 11, 14), grid: { w: 10, h: 4 },
    waterDepthFt: 18, waterDepthMaxFt: 300, waterAxis: 'x', shoreBase: 'Morgan City, La.', distanceToShoreMi: 30,
    lat: 28.9, lon: -91.0, startPlot: 11, recommendedRig: 'rotary',
    leaseBase: 300000, leaseYear: 1950, royalty: 0.1667,
    prorationGroup: 'louisiana',
    concession: { kind: 'lease', bidRound: 'ocs_1954', note: 'Louisiana state leases until the 1953 Submerged Lands/OCS Lands Acts; federal sealed-bid OCS sales from Oct 1954.' },
    boomCurve: [[1947.7, 1], [1948, 2.5], [1950, 2], [1953.5, 2.5], [1962.2, 4], [1970, 3], [1974, 4], [1981, 5], [1986.2, 1], [1990, 1.5]],
    environment: GULF_ENV,
    transport: ['coastal_tanker@1947', 'offshore_pipeline@1951', 'tanker@1951'],
    blurb: 'Kerr-McGee’s Kermac 16 stands ten miles off Louisiana, out of sight of land, on pilings in twenty feet of water. Everyone says it is a stunt.',
    strata: stack([
      [300,   'clay',      'Soft seabed mud',                   1, 8.5, 2, { heaving: true }],
      [1200,  'sand',      'Pleistocene sands & clays',         2, 8.6, 10],
      [1400,  'sand',      'Shallow water sand',                2, 8.9, 8, WATER],
      [2000,  'clay',      'Pleistocene clay',                  2, 8.7, 8, { balling: true }],
      [4000,  'sand',      'Pliocene sands',                    3, 8.8, 15],
      [8000,  'shale',     'Miocene shale',                     3, 8.8, 30],
      [11000, 'sand',      'Miocene sand-shale sequence',       3, 8.8, 20],
      [13000, 'shale',     'Geopressured transition shale',     3, 14.0, 20, { ...GEO(14), frac: 16.5 }],
      [15000, 'sand',      'Deep Miocene sands',                4, 8.8, 20, { frac: 16.8 }],
      [20000, 'salt',      'Louann salt',                       3, 9.8, 20, { salt: true }],
    ]),
    prospects: [
      { cx: 2, cy: 1.5, rx: 1.8, ry: 1.4, reservoirs: [
        { id: 'gs_pliocene', name: 'Pliocene sands (Ship Shoal 32)', top: 2300, dipFtPerPlot: 60, thick: 80, ooip: 3e7, qiRange: [100, 1500], pore: 9.2, gusherChance: 0.05, ...OFF, rf: 0.3, api: 28 },
        { id: 'gs_miocene_a', name: 'Miocene sands (salt-dome flank)', top: 8500, dipFtPerPlot: 80, thick: 150, ooip: 1.5e8, qiRange: [500, 5000], pore: 10.4, gusherChance: 0.1, ...OFF, api: 34 },
      ] },
      { cx: 7, cy: 2, rx: 2.2, ry: 1.5, reservoirs: [
        { id: 'gs_miocene_b', name: 'Miocene sands (Eugene Island trend)', top: 9200, dipFtPerPlot: 80, thick: 200, ooip: 3e8, qiRange: [800, 6000], pore: 10.8, gusherChance: 0.1, ...OFF, api: 35, gasCap: true },
        { id: 'gs_deep', name: 'Deep geopressured sands', top: 13300, dipFtPerPlot: 100, thick: 200, ooip: 2e8, qiRange: [2000, 12000], pore: 14.6, gusherChance: 0.15, ...OFF, api: 45, gasCap: true },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0003, fire: 0.0004, storm: 0.0015 },
  },

  // ======================================================================== Lake Maracaibo (Bolivar Coastal lake fields)
  {
    id: 'maracaibo_lake', name: 'Lake Maracaibo (Bachaquero–Lama)', body: 'earth', region: 'venezuela', regionId: 'venezuela',
    era: 'postwar', opensDay: D(1946, 1, 1), grid: { w: 8, h: 4 },
    waterDepthFt: 15, waterDepthMaxFt: 110, waterAxis: 'x', shoreBase: 'Lagunillas', distanceToShoreMi: 8,
    lat: 10.0, lon: -71.4, startPlot: 9, recommendedRig: 'rotary',
    leaseBase: 40000, leaseYear: 1946, royalty: 0.1667,
    concession: { kind: 'concession', bidRound: 'venezuela_1956', note: 'Fifty-fifty since 1948 (the first in the world). The 1956-57 lake concession round is the last before "no more concessions".' },
    boomCurve: [[1946, 1], [1956.5, 3], [1958, 2], [1960, 1], [1975.9, 0.1]],
    environment: { kind: 'lake', subsidence: true, stormSeason: { months: [8, 9, 10], perYear: 1, hsFt: [3, 6], named: false } },
    transport: ['coastal_tanker@1946', 'offshore_pipeline@1952', 'tanker@1950', 'supertanker_charter@1959'],
    blurb: 'A forest of derricks standing in the lake on concrete piles, gas flares on the water at night. The shoreline is sinking as the oil comes out.',
    strata: stack([
      [100,   'clay',      'Lake-bottom mud',                   1, 8.5, 3],
      [1200,  'clay',      'Onia & La Puerta clays',            2, 8.6, 10],
      [3500,  'sand',      'Lagunillas sands (Bachaquero)',     2, 8.7, 10],
      [4000,  'shale',     'La Rosa shale',                     3, 8.8, 30],
      [8000,  'shale',     'Eocene Paují shales',               3, 8.8, 40],
      [12000, 'sandstone', 'Eocene Misoa sands',                5, 8.8, 60],
      [16000, 'limestone', 'Cretaceous Cogollo & La Luna',      6, 9.8, 200, { gas: true }],
    ]),
    prospects: [
      { cx: 2, cy: 1.5, rx: 2.2, ry: 1.5, reservoirs: [
        { id: 'ml_bachaquero', name: 'Bachaquero (Lagunillas) sands', top: 1600, dipFtPerPlot: 30, thick: 300, ooip: 2e9, qiRange: [200, 3000], pore: 9.4, gusherChance: 0.05,
          ...OFF, rf: 0.18, di: 0.15, api: 17, heavy: true, viscosityCp: 300, eorPotential: { steam: 0.15, waterflood: 0.05 } },
      ] },
      { cx: 6, cy: 1.5, rx: 1.8, ry: 1.4, reservoirs: [
        { id: 'ml_lama', name: 'Eocene B & C sands (Lama, 1957)', top: 9000, dipFtPerPlot: 80, thick: 400, ooip: 1.5e9, qiRange: [1000, 8000], pore: 10.6, gusherChance: 0.15,
          ...OFF, rf: 0.3, di: 0.1, api: 32, eorPotential: { waterflood: 0.12 } },
      ] },
    ],
    seeps: [1, 8],
    hazards: { lightning: 0.0004, fire: 0.0006, subsidence: 0.0002, storm: 0.0002 },
  },

  // ======================================================================== Permian Basin (Wasson & SACROC)
  {
    id: 'permian_basin', name: 'Permian Basin (Wasson & Kelly-Snyder)', body: 'earth', region: 'texas', regionId: 'texas',
    era: 'postwar', opensDay: D(1946, 1, 1), grid: { w: 8, h: 5 }, waterDepthFt: 0,
    lat: 33.0, lon: -102.6, startPlot: 18, prorationGroup: 'texas', recommendedRig: 'rotary',
    leaseBase: 8000, leaseYear: 1946, royalty: 0.125,
    concession: { kind: 'lease', note: 'Fee leases from ranchers. The prize here is not discovery but recovery: unitize, waterflood, then inject CO2.' },
    boomCurve: [[1946, 1], [1948.8, 2], [1953, 1.5], [1972, 1.2], [1974, 2], [1981, 3], [1986.2, 0.6], [1990, 0.8]],
    environment: { kind: 'desert', heatC: 42, stormSeason: { months: [4, 5, 6], perYear: 2, hsFt: [0, 0], named: false, sandstorm: true } },
    transport: ['tank_truck@1946', 'gathering_pipe@1946', 'trunk_pipeline@1946', 'common_carrier_pipeline@1946'],
    blurb: 'Dolomite that holds billions of barrels and gives up fifteen percent of them on its own. Waterflooding was born for rock like this; CO2 floods will be next.',
    strata: stack([
      [1500,  'shale',     'Caliche & Triassic red beds',       3, 8.6, 40],
      [3000,  'salt',      'Salado & Rustler salt',             3, 8.8, 20, { salt: true }],
      [4500,  'anhydrite', 'Yates & Seven Rivers anhydrite',    5, 8.7, 150],
      [5600,  'dolomite',  'San Andres dolomite',               7, 8.7, 200, { h2s: 150 }],
      [6500,  'dolomite',  'Glorieta & Clear Fork',             7, 8.7, 200],
      [6700,  'shale',     'Wolfcamp shale',                    4, 8.8, 60],
      [8000,  'limestone', 'Canyon reef limestone',             6, 8.8, 200],
      [12000, 'dolomite',  'Strawn & Ellenburger',              7, 9.5, 200, { gas: true }],
    ]),
    prospects: [
      { cx: 2.5, cy: 2, rx: 2.6, ry: 1.8, reservoirs: [
        { id: 'pb_san_andres', name: 'San Andres (Wasson Denver Unit)', top: 4900, dipFtPerPlot: 15, thick: 300, ooip: 1.5e9, qiRange: [20, 200], pore: 9.0, gusherChance: 0,
          ...RES, rf: 0.15, di: 0.12, api: 33, sour: true, h2s: 300, eorPotential: { waterflood: 0.18, co2: 0.12 } },
      ] },
      { cx: 6.5, cy: 2, rx: 1.4, ry: 2.0, reservoirs: [
        { id: 'pb_canyon', name: 'Canyon reef (SACROC)', top: 6750, dipFtPerPlot: 30, thick: 600, ooip: 1.0e9, qiRange: [100, 1500], pore: 9.2, gusherChance: 0.02,
          ...RES, rf: 0.2, di: 0.1, api: 41, eorPotential: { waterflood: 0.15, co2: 0.1 } },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0003, fire: 0.0003, heat: 0.0003 },
  },

  // ======================================================================== Zelten (Nasser), Libya
  {
    id: 'zelten', name: 'Zelten, Sirte Basin', body: 'earth', region: 'libya', regionId: 'libya',
    era: 'postwar', opensDay: D(1955, 11, 19), discoveryDay: D(1959, 6, 13), firstExportDay: D(1961, 9, 12), grid: { w: 8, h: 4 }, waterDepthFt: 0,
    lat: 28.95, lon: 19.75, startPlot: 11, recommendedRig: 'rotary',
    leaseBase: 30000, leaseYear: 1955, royalty: 0.125,
    concession: { kind: 'concession', bidRound: 'libya_1955', note: 'Petroleum Law of 1955: many small concessions to many companies, so no single major can sit on the acreage.' },
    boomCurve: [[1955.9, 1], [1959.5, 5], [1961.7, 4], [1965, 3], [1969.7, 1], [1973.7, 0.2]],
    environment: { kind: 'desert', heatC: 50, stormSeason: { months: [3, 4, 5], perYear: 4, hsFt: [0, 0], named: false, sandstorm: true } },
    transport: ['tank_truck@1959', 'trunk_pipeline@1961', 'tanker@1961', 'supertanker_charter@1961', 'vlcc_charter@1966'],
    blurb: 'Esso has drilled empty desert for three years. The Sirte Basin is a gamble on the Paleocene; win it and your barrels sit a few days’ sail from Europe, west of Suez.',
    strata: stack([
      [200,   'sand',      'Serir gravel & sand',               1, 8.5, 3],
      [2500,  'limestone', 'Marada & Oligocene marls',          4, 8.6, 60],
      [4000,  'anhydrite', 'Gir evaporites',                    5, 8.7, 150],
      [5000,  'shale',     'Kheir shale',                       3, 8.8, 40],
      [6600,  'limestone', 'Zelten (Ruaga) limestone',          6, 8.8, 200],
      [9000,  'shale',     'Sirte shale',                       3, 8.9, 40],
      [12000, 'sandstone', 'Nubian sandstone',                  7, 9.2, 80],
    ]),
    prospects: [
      { cx: 3.5, cy: 1.5, rx: 3.0, ry: 1.6, reservoirs: [
        { id: 'ze_zelten', name: 'Zelten limestone', top: 5400, dipFtPerPlot: 30, thick: 325, ooip: 2e9, qiRange: [3000, 17000], pore: 9.4, gusherChance: 0.15,
          ...RES, rf: 0.3, di: 0.08, api: 38, waterDrive: true, eorPotential: { waterflood: 0.12, gas: 0.05 } },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0001, fire: 0.0002, heat: 0.0007 },
  },

  // ======================================================================== Niger Delta (Oloibiri–Bomu), Nigeria
  {
    id: 'niger_delta', name: 'Niger Delta (Oloibiri–Bomu)', body: 'earth', region: 'nigeria', regionId: 'nigeria',
    era: 'postwar', opensDay: D(1956, 1, 1), discoveryDay: D(1956, 1, 15), firstExportDay: D(1958, 2, 17), grid: { w: 8, h: 4 },
    waterDepthFt: 0, waterDepthMaxFt: 15, waterAxis: 'x', shoreBase: 'Port Harcourt', distanceToShoreMi: 0,
    lat: 4.67, lon: 6.32, startPlot: 9, recommendedRig: 'rotary',
    leaseBase: 20000, leaseYear: 1956, royalty: 0.125,
    concession: { kind: 'concession', stateCompany: 'Shell-BP Petroleum Development Co. → NNOC/NNPC participation from 1971', note: 'Swamp and creek country: dredge a canal for a posted barge or fly a heli-rig in.' },
    boomCurve: [[1956, 1], [1956.1, 3], [1958.2, 3], [1967.4, 0.4], [1970.1, 1.5], [1974, 2], [1979.6, 1], [1995, 0.8]],
    environment: { kind: 'swamp', stormSeason: { months: [6, 7, 8, 9, 10], peakMonth: 9, perYear: 2, hsFt: [2, 5], named: false, flood: true } },
    transport: ['coastal_tanker@1958', 'trunk_pipeline@1960', 'tanker@1961', 'supertanker_charter@1961', 'vlcc_charter@1966'],
    blurb: 'Mangrove creeks, malaria and a well at Oloibiri that finally found oil at twelve thousand feet. The delta is a sponge of oil sands, and of trouble.',
    strata: stack([
      [150,   'clay',      'Mangrove mud & peat',               1, 8.5, 2, { heaving: true }],
      [600,   'sand',      'Benin freshwater sands',            1, 8.7, 5, WATER],
      [4000,  'sand',      'Benin Formation sands',             2, 8.6, 10],
      [11000, 'sand',      'Agbada sand-shale sequence',        3, 8.8, 20],
      [13000, 'sand',      'Lower Agbada sands',                4, 8.8, 20, { frac: 16.5 }],
      [16000, 'shale',     'Akata shale (geopressured)',        3, 13.5, 20, { ...GEO(13.5), frac: 16 }],
    ]),
    prospects: [
      { cx: 1.5, cy: 1.5, rx: 1.4, ry: 1.2, reservoirs: [
        { id: 'nd_oloibiri', name: 'Oloibiri Agbada sands', top: 11800, dipFtPerPlot: 60, thick: 100, ooip: 6e7, qiRange: [500, 5000], pore: 11.2, gusherChance: 0.1,
          ...RES, rf: 0.3, di: 0.3, api: 36 },
      ] },
      { cx: 5.5, cy: 2, rx: 2.0, ry: 1.4, reservoirs: [
        { id: 'nd_bomu', name: 'Bomu–Forcados Agbada sands', top: 7500, dipFtPerPlot: 60, thick: 200, ooip: 8e8, qiRange: [1000, 8000], pore: 9.8, gusherChance: 0.1,
          ...RES, rf: 0.35, di: 0.12, api: 35, waterDrive: true, gasCap: true },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0005, fire: 0.0005, disease: 0.0005, storm: 0.0002 },
  },

  // ======================================================================== Niger Delta shallow offshore (Gulf of Guinea)
  {
    id: 'bonny_offshore', name: 'Niger Delta Offshore (Bonny–Okan)', body: 'earth', region: 'nigeria', regionId: 'nigeria',
    era: 'offshore', opensDay: D(1965, 1, 1), discoveredYear: 1964, grid: { w: 6, h: 4 },
    waterDepthFt: 120, waterDepthMaxFt: 300, waterAxis: 'x', shoreBase: 'Bonny', distanceToShoreMi: 25,
    lat: 4.05, lon: 5.65, startPlot: 9, recommendedRig: 'rotary',
    leaseBase: 60000, leaseYear: 1965, royalty: 0.125,
    concession: { kind: 'concession', stateCompany: 'Gulf Oil / Shell-BP offshore leases → NNPC participation from 1971', note: 'Gulf Oil’s Okan field came on in 1965 off the Escravos bar. Calm water and warm seas — and, from the 1990s, armed men in fast boats.' },
    boomCurve: [[1965, 1], [1966, 2], [1967.4, 0.5], [1970.1, 1.5], [1974, 2], [1986, 0.8], [2000, 1.2]],
    environment: { kind: 'shelf', stormSeason: { months: [6, 7, 8, 9], perYear: 2, hsFt: [3, 8], named: false } },
    transport: ['coastal_tanker@1965', 'tanker@1965', 'offshore_pipeline@1968', 'vlcc_charter@1970', 'shuttle_tanker@1975'],
    blurb: 'Twenty-five miles off the Bonny bar the Agbada sands go on under the sea. Warm water, short tows, cheap jackets — and a coast where piracy and militancy grew up alongside the oil.',
    strata: stack([
      [300,   'clay',      'Seabed muds & shell sand',          1, 8.5, 2, { heaving: true }],
      [3500,  'sand',      'Benin Formation sands',             2, 8.6, 10],
      [9500,  'sand',      'Agbada sand-shale sequence',        3, 8.8, 20],
      [12000, 'sand',      'Lower Agbada sands',                4, 8.8, 20, { frac: 16.2 }],
      [16000, 'shale',     'Akata shale (geopressured)',        3, 13.8, 20, { ...GEO(13.8), frac: 16 }],
    ]),
    prospects: [
      { cx: 2, cy: 1.5, rx: 1.8, ry: 1.3, reservoirs: [
        { id: 'bo_okan', name: 'Okan Agbada sands', top: 7200, dipFtPerPlot: 55, thick: 180, ooip: 7e8, qiRange: [2000, 9000], pore: 9.9, gusherChance: 0.08,
          ...RES, rf: 0.35, di: 0.14, api: 34, waterDrive: true },
      ] },
      { cx: 4.6, cy: 2.2, rx: 1.4, ry: 1.2, reservoirs: [
        { id: 'bo_meren', name: 'Meren deeper Agbada sands', top: 9800, dipFtPerPlot: 70, thick: 120, ooip: 4e8, qiRange: [1500, 7000], pore: 10.8, gusherChance: 0.1,
          ...RES, rf: 0.32, di: 0.18, api: 36 },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0004, fire: 0.0004, storm: 0.0002 },
  },

  // ======================================================================== Australia: Bass Strait (Gippsland Basin)
  {
    id: 'gippsland', name: 'Bass Strait (Gippsland: Kingfish–Halibut–Barracouta)', body: 'earth', region: 'australia', regionId: 'australia',
    era: 'offshore', opensDay: D(1965, 2, 1), discoveredYear: 1965, grid: { w: 6, h: 4 },
    waterDepthFt: 250, waterDepthMaxFt: 400, waterAxis: 'x', shoreBase: 'Barry Beach / Longford', distanceToShoreMi: 50,
    lat: -38.4, lon: 148.2, startPlot: 7, recommendedRig: 'rotary',
    leaseBase: 80000, leaseYear: 1965, royalty: 0.1,
    concession: { kind: 'concession', stateCompany: 'Esso Australia–BHP (50:50) permits', note: 'BHP brought Esso in in 1964; Barracouta gas in Feb 1965, then Marlin, and the giant Kingfish (1967) and Halibut oil fields.' },
    boomCurve: [[1965.1, 1], [1967.3, 3], [1969.8, 2], [1975, 1.4], [1985, 1]],
    environment: { kind: 'shelf', winterStorms: { months: [5, 6, 7, 8, 9], perYear: 6, hsFt: [12, 32] }, currentKn: 1.5 },
    transport: ['coastal_tanker@1969', 'offshore_pipeline@1969', 'tanker@1970'],
    blurb: 'The Roaring Forties funnel through Bass Strait: short steep seas and gales that close the helideck. Under the Lakes Entrance shale, the Latrobe sands hold Australia’s oil.',
    strata: stack([
      [3200,  'limestone', 'Seaspray Group marls & limestone',   4, 8.6, 30, { frac: 12.0 }],
      [4300,  'shale',     'Lakes Entrance shale (seal)',        3, 8.7, 20, { frac: 12.6 }],
      [8500,  'sandstone', 'Latrobe Group sands & coals',        4, 8.8, 25, { frac: 13.4 }],
      [12000, 'sandstone', 'Golden Beach & Strzelecki beds',     6, 9.0, 120, { frac: 14.6 }],
    ]),
    prospects: [
      { cx: 1.6, cy: 1.5, rx: 1.5, ry: 1.2, reservoirs: [
        { id: 'gp_kingfish', name: 'Kingfish Latrobe sands', top: 7400, dipFtPerPlot: 40, thick: 300, ooip: 1.6e9, qiRange: [6000, 24000], pore: 9.4, gusherChance: 0.04,
          ...OFF, rf: 0.4, di: 0.14, api: 46, waterDrive: true },
      ] },
      { cx: 4.3, cy: 2.2, rx: 1.5, ry: 1.3, reservoirs: [
        { id: 'gp_halibut', name: 'Halibut Latrobe sands', top: 7600, dipFtPerPlot: 45, thick: 250, ooip: 1.2e9, qiRange: [5000, 20000], pore: 9.4, gusherChance: 0.04,
          ...OFF, rf: 0.4, di: 0.14, api: 43, waterDrive: true },
        { id: 'gp_barracouta', name: 'Barracouta gas-condensate (top Latrobe)', top: 3450, dipFtPerPlot: 30, thick: 150, ooip: 1.5e8, qiRange: [800, 4000], pore: 8.9, gusherChance: 0.06,
          ...OFF, rf: 0.4, di: 0.25, api: 55, gasCap: true },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0003, fire: 0.0004, storm: 0.0006 },
  },

  // ======================================================================== Australia: Otway Basin (offshore Victoria)
  {
    id: 'otway', name: 'Otway Basin (Thylacine–Geographe–Minerva)', body: 'earth', region: 'australia', regionId: 'australia',
    era: 'offshore', opensDay: D(1999, 6, 1), discoveredYear: 1993, grid: { w: 6, h: 4 },
    waterDepthFt: 330, waterDepthMaxFt: 650, waterAxis: 'x', shoreBase: 'Port Campbell', distanceToShoreMi: 40,
    lat: -38.9, lon: 142.0, startPlot: 9, recommendedRig: 'rotary',
    leaseBase: 3e6, leaseYear: 1999, royalty: 0.1,
    concession: { kind: 'concession', bidRound: null, stateCompany: 'Woodside / Origin / BHP permits', note: 'BHP found Minerva in 1993; Woodside’s Thylacine and Geographe (2001) feed the Otway gas plant onshore near Port Campbell. The game sells the condensate.' },
    boomCurve: [[1999.4, 1], [2001, 1.4], [2004, 1.6], [2007, 1.3]],
    environment: { kind: 'deepwater', winterStorms: { months: [5, 6, 7, 8, 9, 10], perYear: 7, hsFt: [15, 38] }, currentKn: 1.2 },
    transport: ['coastal_tanker@1999', 'offshore_pipeline@2004'],
    blurb: 'Southern Ocean swell rolls in unbroken from Africa onto the Shipwreck Coast. Below the Belfast mudstone, the Waarre sands hold gas and light condensate.',
    strata: stack([
      [3000,  'limestone', 'Heytesbury & Nirranda carbonates',  4, 8.6, 30, { frac: 11.8 }],
      [4800,  'shale',     'Wangerrip & Belfast mudstone (seal)', 3, 8.7, 20, { frac: 12.6 }],
      [7200,  'sandstone', 'Waarre Sandstone (Sherbrook Gp)',   4, 8.8, 30, { frac: 13.6 }],
      [12000, 'sandstone', 'Eumeralla Formation',               5, 9.2, 60, { frac: 14.8 }],
    ]),
    prospects: [
      { cx: 2, cy: 1.6, rx: 1.8, ry: 1.3, reservoirs: [
        { id: 'ot_thylacine', name: 'Thylacine Waarre gas-condensate', top: 6200, dipFtPerPlot: 45, thick: 200, ooip: 2.5e8, qiRange: [1500, 6000], pore: 9.2, gusherChance: 0.05,
          ...OFF, rf: 0.4, di: 0.22, api: 55, gasCap: true },
      ] },
      { cx: 4.7, cy: 2.0, rx: 1.2, ry: 1.1, reservoirs: [
        { id: 'ot_minerva', name: 'Minerva Waarre gas-condensate', top: 5800, dipFtPerPlot: 40, thick: 120, ooip: 1e8, qiRange: [800, 4000], pore: 9.1, gusherChance: 0.05,
          ...OFF, rf: 0.4, di: 0.25, api: 54, gasCap: true },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0002, fire: 0.0003, storm: 0.0007 },
  },

  // ======================================================================== Australia: North West Shelf (Carnarvon Basin)
  {
    id: 'north_west_shelf', name: 'North West Shelf (North Rankin–Goodwyn)', body: 'earth', region: 'australia', regionId: 'australia',
    era: 'offshore', opensDay: D(1971, 6, 1), discoveredYear: 1971, grid: { w: 6, h: 4 },
    waterDepthFt: 410, waterDepthMaxFt: 480, waterAxis: 'x', shoreBase: 'Karratha / Withnell Bay', distanceToShoreMi: 80,
    lat: -19.6, lon: 116.1, startPlot: 9, recommendedRig: 'rotary',
    leaseBase: 300000, leaseYear: 1971, royalty: 0.1,
    concession: { kind: 'concession', stateCompany: 'Woodside–Burmah, later the North West Shelf joint venture (Shell, BP, BHP, Chevron, Mitsubishi/Mitsui)', note: 'North Rankin, found in 1971, is one of the world’s great gas fields; the platform and Karratha gas plant came on in 1984, LNG to Japan from 1989. The game sells the condensate.' },
    boomCurve: [[1971.4, 1], [1984, 2], [1989, 2.5], [2000, 1.5]],
    environment: { kind: 'shelf', stormSeason: { months: [12, 1, 2, 3, 4], peakMonth: 2, perYear: 3, hsFt: [8, 32], named: true } },
    transport: ['shuttle_tanker@1975', 'offshore_pipeline@1984', 'tanker@1984'],
    blurb: 'Warm Indian Ocean water and cyclones every summer. Deep under the Muderong shale, the Triassic Mungaroo sands hold trillions of cubic feet — and a light condensate that sells like oil.',
    strata: stack([
      [3500,  'limestone', 'Cenozoic carbonates',                4, 8.6, 30, { frac: 12.2 }],
      [7800,  'shale',     'Barrow Group & Gearle siltstone',    3, 8.8, 20, { frac: 13.4 }],
      [8400,  'shale',     'Muderong shale (seal)',              3, 8.7, 20, { frac: 13.8 }],
      [12500, 'sandstone', 'Mungaroo Formation sands',           5, 8.8, 40, { frac: 15.0 }],
      [16000, 'shale',     'Locker shale',                       4, 9.6, 40, { frac: 15.6 }],
    ]),
    prospects: [
      { cx: 2.2, cy: 1.6, rx: 2.0, ry: 1.4, reservoirs: [
        { id: 'nw_north_rankin', name: 'North Rankin Mungaroo gas-condensate', top: 8600, dipFtPerPlot: 35, thick: 600, ooip: 1.2e9, qiRange: [4000, 16000], pore: 9.4, gusherChance: 0.04,
          ...OFF, rf: 0.4, di: 0.12, api: 52, gasCap: true },
      ] },
      { cx: 4.8, cy: 2.2, rx: 1.2, ry: 1.2, reservoirs: [
        { id: 'nw_goodwyn', name: 'Goodwyn Mungaroo gas-condensate', top: 9200, dipFtPerPlot: 50, thick: 400, ooip: 6e8, qiRange: [3000, 12000], pore: 9.6, gusherChance: 0.04,
          ...OFF, rf: 0.4, di: 0.14, api: 54, gasCap: true },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0004, fire: 0.0004, storm: 0.0008 },
  },

  // ======================================================================== Hassi Messaoud, Algeria
  {
    id: 'hassi_messaoud', name: 'Hassi Messaoud, Sahara', body: 'earth', region: 'algeria', regionId: 'algeria',
    era: 'postwar', opensDay: D(1953, 11, 1), discoveryDay: D(1956, 6, 16), firstExportDay: D(1959, 11, 1), grid: { w: 8, h: 5 }, waterDepthFt: 0,
    lat: 31.67, lon: 6.07, startPlot: 19, recommendedRig: 'rotary',
    leaseBase: 25000, leaseYear: 1953, royalty: 0.125,
    concession: { kind: 'concession', stateCompany: 'SN Repal & CFP(A) → Sonatrach (1963)', note: 'French Sahara permits during the Algerian War; 51% nationalized 24 Feb 1971.' },
    boomCurve: [[1953.8, 1], [1956.5, 4], [1959.9, 3], [1962.5, 1.5], [1971.15, 0.2]],
    environment: { kind: 'desert', heatC: 52, stormSeason: { months: [3, 4, 5], perYear: 5, hsFt: [0, 0], named: false, sandstorm: true } },
    transport: ['tank_truck@1957', 'trunk_pipeline@1959', 'tanker@1959', 'supertanker_charter@1960'],
    blurb: 'Two miles under the dunes a Cambrian quartzite holds light, sweet oil. The rock dulls a bit in hours; the war cuts the pipeline at night.',
    strata: stack([
      [300,   'sand',      'Erg dunes',                         1, 8.5, 2],
      [1500,  'sandstone', 'Mio-Pliocene sandstone & marl',     3, 8.6, 40],
      [5500,  'salt',      'Senonian salt & anhydrite',         3, 8.8, 20, { salt: true }],
      [10700, 'shale',     'Triassic & Ordovician shales',      4, 8.8, 60],
      [13000, 'sandstone', 'Cambrian quartzitic sandstone',     8, 8.8, 300],
      [16000, 'granite',   'Precambrian basement',              9, 8.8, 400],
    ]),
    prospects: [
      { cx: 3.5, cy: 2, rx: 3.2, ry: 2.2, reservoirs: [
        { id: 'hm_cambrian', name: 'Cambrian Ra–Ri sandstone', top: 10800, dipFtPerPlot: 40, thick: 400, ooip: 3e9, qiRange: [1000, 6000], pore: 9.6, gusherChance: 0.05,
          ...RES, rf: 0.22, di: 0.08, api: 45, eorPotential: { gas: 0.1, waterflood: 0.06 } },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0001, fire: 0.0002, heat: 0.0008 },
  },

  // ======================================================================== Prudhoe Bay, Alaska North Slope
  {
    id: 'prudhoe_bay', name: 'Prudhoe Bay, North Slope', body: 'earth', region: 'alaska', regionId: 'alaska',
    era: 'postwar', opensDay: D(1968, 1, 1), discoveryDay: D(1968, 3, 12), firstExportDay: D(1977, 6, 20), grid: { w: 8, h: 4 }, waterDepthFt: 0,
    lat: 70.3, lon: -148.5, startPlot: 11, recommendedRig: 'rotary',
    leaseBase: 2e6, leaseYear: 1969, royalty: 0.125,
    concession: { kind: 'lease', bidRound: 'alaska_sale_23', note: 'State of Alaska leases. Lease Sale 23 (10 Sept 1969) took $900 million in sealed bonus bids in one day.' },
    outletNote: 'No outlet until the Trans-Alaska Pipeline opens (20 June 1977). Drill, test and shut in; or fund the pipeline consortium.',
    boomCurve: [[1968.1, 1], [1968.3, 4], [1969.7, 10], [1970, 6], [1973.9, 4], [1977.5, 3], [1986.2, 1]],
    environment: { kind: 'arctic', coldC: -50, ice: { kind: 'permafrost', months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], drillSeason: [12, 1, 2, 3, 4] },
                   winterStorms: { months: [11, 12, 1, 2, 3], perYear: 6, hsFt: [0, 0], blizzard: true }, darkness: { months: [11, 12, 1] } },
    transport: ['taps_pipeline@1977'],
    blurb: 'ARCO and Humble’s discovery well flared at 1,152 barrels a day from the Sadlerochit. North America’s largest field, 800 miles from an ice-free port.',
    strata: stack([
      [1900,  'gravel',    'Permafrost gravels & ice',          4, 8.5, 30, { permafrost: true }],
      [4500,  'sand',      'Sagavanirktok sands',               3, 8.6, 15],
      [6000,  'shale',     'Colville shale',                    3, 8.7, 40],
      [6600,  'sand',      'Kuparuk sand',                      4, 8.8, 25],
      [8200,  'shale',     'Kingak & Shublik shale',            4, 8.8, 40],
      [9600,  'sandstone', 'Sadlerochit (Ivishak) sandstone',   5, 8.8, 60],
      [12000, 'limestone', 'Lisburne limestone',                6, 8.8, 200],
    ]),
    prospects: [
      { cx: 3.5, cy: 1.5, rx: 3.4, ry: 1.6, reservoirs: [
        { id: 'pr_sadlerochit', name: 'Sadlerochit (Ivishak)', top: 8500, dipFtPerPlot: 30, thick: 450, ooip: 4e9, qiRange: [2000, 12000], pore: 9.4, gusherChance: 0.05,
          ...RES, rf: 0.4, di: 0.07, api: 27, gasCap: true, eorPotential: { waterflood: 0.1, gas: 0.06 } },
        { id: 'pr_lisburne', name: 'Lisburne limestone', top: 9800, dipFtPerPlot: 30, thick: 300, ooip: 6e8, qiRange: [300, 2000], pore: 9.6, gusherChance: 0.02, ...RES, rf: 0.2, di: 0.15, api: 27 },
      ] },
      { cx: 7, cy: 1, rx: 1.0, ry: 1.0, reservoirs: [
        { id: 'pr_kuparuk', name: 'Kuparuk River sand', top: 6100, dipFtPerPlot: 25, thick: 150, ooip: 1e9, qiRange: [500, 3000], pore: 9.2, gusherChance: 0.02,
          ...RES, rf: 0.3, di: 0.1, api: 24, eorPotential: { waterflood: 0.12 } },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0, fire: 0.0003, cold: 0.002 },
  },

  // ======================================================================== Ekofisk, Norwegian North Sea
  {
    id: 'ekofisk', name: 'Ekofisk, Norwegian North Sea', body: 'earth', region: 'north_sea_norway', regionId: 'north_sea_norway',
    era: 'postwar', opensDay: D(1965, 8, 13), discoveryDay: D(1969, 12, 23), firstExportDay: D(1971, 6, 15), grid: { w: 6, h: 4 },
    waterDepthFt: 230, waterDepthMaxFt: 260, waterAxis: 'x', shoreBase: 'Stavanger', distanceToShoreMi: 180,
    lat: 56.55, lon: 3.21, startPlot: 9, recommendedRig: 'rotary',
    leaseBase: 120000, leaseYear: 1965, royalty: 0.12,
    concession: { kind: 'licence', bidRound: 'norway_r1_1965', stateCompany: 'Statoil (from 1972)', note: 'First Norwegian licensing round (1965): discretionary awards for work programmes. Statoil carried interest from 1972.' },
    boomCurve: [[1965.6, 1], [1969.95, 6], [1971.5, 5], [1977.3, 3], [1986.2, 1]],
    environment: { ...NORTH_SEA_ENV, subsidence: true },
    transport: ['shuttle_tanker@1971', 'offshore_pipeline@1975'],
    blurb: 'Phillips has drilled thirty-two dry holes in the Norwegian sector and the rig is due to leave. One last well into the chalk.',
    strata: stack([
      [100,   'sand',      'Seabed sand',                       1, 8.5, 3],
      [3500,  'clay',      'Nordland clays',                    2, 8.7, 10, { balling: true }],
      [8500,  'shale',     'Hordaland shale',                   3, 8.8, 30],
      [9600,  'shale',     'Overpressured Paleocene shale',     3, 13.0, 25, { ...GEO(13), frac: 15 }],
      [11000, 'chalk',     'Ekofisk & Tor chalk',               4, 8.8, 60, { frac: 15.2 }],
      [15000, 'shale',     'Lower Cretaceous & Jurassic shale', 4, 9.5, 60],
    ]),
    prospects: [
      { cx: 2.5, cy: 1.5, rx: 2.4, ry: 1.5, reservoirs: [
        { id: 'ek_chalk', name: 'Ekofisk & Tor chalk', top: 9700, dipFtPerPlot: 50, thick: 500, ooip: 3e9, qiRange: [3000, 10000], pore: 13.4, gusherChance: 0.1,
          ...OFF, rf: 0.2, di: 0.15, api: 38, compacting: true, eorPotential: { waterflood: 0.2 } },
      ] },
      { cx: 5, cy: 2.5, rx: 1.0, ry: 1.0, reservoirs: [
        { id: 'ek_west', name: 'West Ekofisk chalk', top: 9800, dipFtPerPlot: 50, thick: 400, ooip: 4e8, qiRange: [2000, 7000], pore: 13.2, gusherChance: 0.1, ...OFF, rf: 0.2, api: 40, compacting: true },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0001, fire: 0.0004, storm: 0.002, subsidence: 0.0002 },
  },

  // ======================================================================== Forties, UK North Sea
  {
    id: 'forties', name: 'Forties, UK North Sea', body: 'earth', region: 'north_sea_uk', regionId: 'north_sea_uk',
    era: 'postwar', opensDay: D(1965, 8, 1), discoveryDay: D(1970, 10, 7), firstExportDay: D(1975, 11, 3), grid: { w: 6, h: 4 },
    waterDepthFt: 350, waterDepthMaxFt: 420, waterAxis: 'x', shoreBase: 'Aberdeen', distanceToShoreMi: 110,
    lat: 57.73, lon: 0.98, startPlot: 9, recommendedRig: 'rotary',
    leaseBase: 120000, leaseYear: 1965, royalty: 0.125,
    concession: { kind: 'licence', bidRound: 'uk_r2_1965', note: 'UK licensing rounds: discretionary awards for a work programme. Petroleum Revenue Tax from 1975.' },
    boomCurve: [[1965.6, 1], [1970.8, 5], [1975.8, 4], [1986.2, 1.2]],
    environment: NORTH_SEA_ENV,
    transport: ['shuttle_tanker@1975', 'offshore_pipeline@1975'],
    blurb: 'BP’s Sea Quest is drilling block 21/10 in four hundred feet of North Sea. The geologists are talking about a Paleocene sand the size of a county.',
    strata: stack([
      [100,   'sand',      'Seabed sand',                       1, 8.5, 3],
      [2500,  'clay',      'Quaternary & Pliocene clays',       2, 8.6, 10],
      [6500,  'shale',     'Tertiary shales',                   3, 8.8, 30],
      [7800,  'sandstone', 'Forties sandstone',                 4, 8.8, 40],
      [10000, 'chalk',     'Cretaceous chalk',                  5, 8.9, 80],
      [14000, 'shale',     'Jurassic shale',                    4, 9.6, 60],
    ]),
    prospects: [
      { cx: 2.5, cy: 1.5, rx: 2.4, ry: 1.4, reservoirs: [
        { id: 'fo_forties', name: 'Forties sandstone', top: 6900, dipFtPerPlot: 40, thick: 400, ooip: 4e9, qiRange: [4000, 16000], pore: 9.4, gusherChance: 0.05,
          ...OFF, rf: 0.4, di: 0.12, api: 37, waterDrive: true },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0001, fire: 0.0004, storm: 0.002 },
  },

  // ======================================================================== Brent, UK North Sea
  {
    id: 'brent', name: 'Brent, East Shetland Basin', body: 'earth', region: 'north_sea_uk', regionId: 'north_sea_uk',
    era: 'postwar', opensDay: D(1970, 6, 1), discoveryDay: D(1971, 7, 1), firstExportDay: D(1976, 11, 13), grid: { w: 6, h: 4 },
    waterDepthFt: 440, waterDepthMaxFt: 480, waterAxis: 'x', shoreBase: 'Sullom Voe / Lerwick', distanceToShoreMi: 115,
    lat: 61.05, lon: 1.72, startPlot: 9, recommendedRig: 'rotary',
    leaseBase: 180000, leaseYear: 1970, royalty: 0.125,
    concession: { kind: 'licence', bidRound: 'uk_r4_1971', note: 'Shell/Esso’s 211/29. Its name becomes the world’s price benchmark.' },
    boomCurve: [[1970.4, 1], [1971.6, 5], [1976.9, 4], [1986.2, 1.2]],
    environment: { ...NORTH_SEA_ENV, winterStorms: { months: [10, 11, 12, 1, 2, 3], perYear: 10, hsFt: [20, 55] } },
    transport: ['shuttle_tanker@1976', 'offshore_pipeline@1978'],
    blurb: 'Halfway to Norway north of the Shetlands, in water too deep for any jackup of 1971. Middle Jurassic deltas under a quarter-mile of sea.',
    strata: stack([
      [100,   'sand',      'Seabed sand',                       1, 8.5, 3],
      [6500,  'shale',     'Tertiary clays & shale',            3, 8.7, 20],
      [8400,  'shale',     'Cretaceous shale & marl',           4, 8.8, 40],
      [10000, 'sandstone', 'Brent Group sands',                 5, 8.8, 60],
      [10800, 'shale',     'Dunlin shale',                      4, 8.8, 40],
      [12000, 'sandstone', 'Statfjord Formation',               5, 8.8, 60],
      [15000, 'shale',     'Triassic red beds',                 5, 9.5, 60],
    ]),
    prospects: [
      { cx: 2.5, cy: 1.5, rx: 1.4, ry: 1.8, reservoirs: [
        { id: 'br_brent', name: 'Brent sands', top: 8700, dipFtPerPlot: 50, thick: 600, ooip: 3.8e9, qiRange: [3000, 15000], pore: 9.8, gusherChance: 0.05,
          ...OFF, rf: 0.4, di: 0.12, api: 38, gasCap: true, eorPotential: { waterflood: 0.1, gas: 0.05 } },
        { id: 'br_statfjord', name: 'Statfjord sands', top: 10900, dipFtPerPlot: 40, thick: 600, ooip: 1.2e9, qiRange: [2000, 8000], pore: 10.0, gusherChance: 0.05, ...OFF, rf: 0.35, api: 38 },
      ] },
      { cx: 5, cy: 1.5, rx: 0.9, ry: 1.4, reservoirs: [
        { id: 'br_east', name: 'Brent sands (Ninian trend)', top: 9000, dipFtPerPlot: 50, thick: 400, ooip: 1.0e9, qiRange: [2000, 10000], pore: 9.8, gusherChance: 0.05, ...OFF, rf: 0.38, api: 36 },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0001, fire: 0.0004, storm: 0.0025 },
  },

  // ======================================================================== Piper, UK North Sea (Outer Moray Firth)
  {
    id: 'piper', name: 'Piper & Claymore, Outer Moray Firth', body: 'earth', region: 'north_sea_uk', regionId: 'north_sea_uk',
    era: 'postwar', opensDay: D(1972, 3, 1), discoveryDay: D(1973, 1, 15), firstExportDay: D(1976, 12, 1), grid: { w: 6, h: 4 },
    waterDepthFt: 470, waterDepthMaxFt: 480, waterAxis: 'x', shoreBase: 'Aberdeen / Flotta', distanceToShoreMi: 120,
    lat: 58.47, lon: 0.25, startPlot: 9, recommendedRig: 'rotary',
    leaseBase: 200000, leaseYear: 1972, royalty: 0.125,
    concession: { kind: 'licence', bidRound: 'uk_r4_1971', note: 'Occidental’s consortium (with Getty, Allied Chemical and Thomson). Piper Alpha also compresses gas from Tartan and Claymore.' },
    boomCurve: [[1972.2, 1], [1973.1, 4], [1976.9, 3], [1988.5, 0.5], [1993, 0.8]],
    environment: NORTH_SEA_ENV,
    transport: ['offshore_pipeline@1976', 'shuttle_tanker@1976'],
    blurb: 'A clean Jurassic sandstone and a pipeline to Flotta in Orkney. The platform that produces it will one day be the worst offshore disaster in history.',
    strata: stack([
      [100,   'sand',      'Seabed sand',                       1, 8.5, 3],
      [6000,  'shale',     'Tertiary & Cretaceous shale',       3, 8.7, 25],
      [7500,  'shale',     'Kimmeridge Clay',                   4, 8.8, 30],
      [8900,  'sandstone', 'Piper sandstone',                   5, 8.8, 60],
      [12000, 'sandstone', 'Triassic & Permian',                6, 9.2, 80],
    ]),
    prospects: [
      { cx: 2.5, cy: 1.5, rx: 2.2, ry: 1.4, reservoirs: [
        { id: 'pi_piper', name: 'Piper sandstone', top: 7700, dipFtPerPlot: 40, thick: 600, ooip: 1.6e9, qiRange: [5000, 20000], pore: 9.6, gusherChance: 0.05,
          ...OFF, rf: 0.4, di: 0.15, api: 37, waterDrive: true },
      ] },
      { cx: 5, cy: 2.5, rx: 1.0, ry: 1.0, reservoirs: [
        { id: 'pi_claymore', name: 'Claymore sandstone', top: 7900, dipFtPerPlot: 40, thick: 500, ooip: 1.4e9, qiRange: [3000, 12000], pore: 9.6, gusherChance: 0.05, ...OFF, rf: 0.33, api: 30, sour: true, h2s: 40 },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0001, fire: 0.0006, storm: 0.002 },
  },

  // ======================================================================== Cantarell, Bay of Campeche (PEMEX)
  {
    id: 'cantarell', name: 'Cantarell (Akal), Bay of Campeche', body: 'earth', region: 'mexico', regionId: 'mexico',
    era: 'offshore', opensDay: D(1976, 1, 1), discoveryDay: D(1976, 6, 1), firstExportDay: D(1979, 6, 1), grid: { w: 6, h: 4 },
    waterDepthFt: 100, waterDepthMaxFt: 130, waterAxis: 'x', shoreBase: 'Ciudad del Carmen', distanceToShoreMi: 50,
    lat: 19.4, lon: -92.25, startPlot: 9, recommendedRig: 'rotary',
    leaseBase: 1, leaseYear: 1976, royalty: 0.125,
    concession: { kind: 'service_only', stateCompany: 'PEMEX', note: 'Mexico has been closed to foreign operators since 1938. PEMEX charters foreign jackups and crews at day rates.' },
    serviceOnly: { operator: 'PEMEX', from: 1976, until: 2014 },
    boomCurve: null,
    environment: { kind: 'shelf', stormSeason: { months: [6, 7, 8, 9, 10], peakMonth: 9, perYear: 0.3, hsFt: [12, 35], named: true },
                   winterStorms: { months: [10, 11, 12, 1, 2, 3], perYear: 6, hsFt: [8, 16], nortes: true } },
    transport: ['tanker@1979', 'offshore_pipeline@1979', 'supertanker_charter@1979', 'vlcc_charter@1979'],
    blurb: 'A fisherman named Rudesindo Cantarell complained of oil fouling his nets. Under it: a shattered breccia from the day the dinosaurs died, holding a supergiant.',
    strata: stack([
      [1500,  'clay',      'Recent & Pliocene clays',           2, 8.6, 10],
      [3500,  'shale',     'Miocene & Oligocene shale',         3, 8.7, 30],
      [4300,  'shale',     'Paleocene shale',                   3, 8.8, 30],
      [7500,  'dolomite',  'K-T boundary breccia (dolomitized)', 6, 8.8, 120, { lossZone: true, frac: 11.5, h2s: 300 }],
      [10000, 'limestone', 'Jurassic Kimmeridgian limestone',   6, 9.0, 200],
      [14000, 'salt',      'Jurassic salt',                     3, 9.8, 20, { salt: true }],
    ]),
    prospects: [
      { cx: 2.5, cy: 1.5, rx: 2.4, ry: 1.6, reservoirs: [
        { id: 'ca_akal', name: 'Akal breccia', top: 4600, dipFtPerPlot: 60, thick: 1000, ooip: 8e9, qiRange: [10000, 40000], pore: 9.6, gusherChance: 0.1,
          ...OFF, rf: 0.4, di: 0.05, api: 22, sour: true, h2s: 300, gasCap: true, eorPotential: { gas: 0.1 } },
      ] },
    ],
    seeps: [8, 9],
    hazards: { lightning: 0.0003, fire: 0.0004, storm: 0.001 },
  },

  // ======================================================================== Samotlor, West Siberia (JV from 1991)
  {
    id: 'samotlor', name: 'Samotlor, West Siberia', body: 'earth', region: 'west_siberia', regionId: 'west_siberia',
    era: 'offshore', opensDay: D(1991, 1, 1), discoveredYear: 1965, grid: { w: 8, h: 5 }, waterDepthFt: 0,
    lat: 61.13, lon: 76.75, startPlot: 19, recommendedRig: 'rotary',
    leaseBase: 1.5e6, leaseYear: 1991, royalty: 0.2,
    concession: { kind: 'jv', stateCompany: 'Nizhnevartovskneftegaz', note: 'Soviet and then Russian joint ventures (White Nights, 1991): bring Western workovers and pumps to a waterlogged supergiant.' },
    boomCurve: [[1991, 1], [1992, 2], [1995, 1.5], [1998.6, 0.5], [1999.9, 1.5]],
    environment: { kind: 'arctic', coldC: -45, ice: { kind: 'bog', months: [11, 12, 1, 2, 3, 4], drillSeason: [11, 12, 1, 2, 3, 4] } },
    transport: ['trunk_pipeline@1991', 'rail@1991'],
    blurb: 'The "dead lake" bog that fed the Soviet Union. It produced three million barrels a day in 1980 and has been flooded with water ever since.',
    strata: stack([
      [30,    'clay',      'Peat bog',                          1, 8.5, 2, { heaving: true }],
      [1500,  'clay',      'Quaternary & Paleogene clays',      2, 8.6, 10],
      [3000,  'sand',      'Pokur aquifer sands',               3, 8.8, 15, WATER],
      [4900,  'shale',     'Kuznetsov & Alym shale',            3, 8.7, 40],
      [5400,  'sand',      'AV sands (Alym)',                   4, 8.8, 25],
      [5500,  'shale',     'Shale parting',                     3, 8.8, 40],
      [6900,  'sand',      'BV (Vartov) sands',                 4, 8.8, 25],
      [9000,  'shale',     'Bazhenov & Jurassic shale',         4, 9.2, 60],
    ]),
    prospects: [
      { cx: 3.5, cy: 2, rx: 3.4, ry: 2.2, reservoirs: [
        { id: 'sm_av', name: 'AV sands', top: 4950, dipFtPerPlot: 20, thick: 150, ooip: 1.5e9, qiRange: [50, 600], pore: 9.2, gusherChance: 0, ...RES, rf: 0.35, di: 0.15, api: 35,
          startRemainingFrac: 0.45, startWaterCut: 0.7, eorPotential: { waterflood: 0.05 } },
        { id: 'sm_bv', name: 'BV (Vartov) sands', top: 5700, dipFtPerPlot: 25, thick: 300, ooip: 6e9, qiRange: [100, 1500], pore: 9.3, gusherChance: 0, ...RES, rf: 0.4, di: 0.15, api: 36,
          startRemainingFrac: 0.4, startWaterCut: 0.75, eorPotential: { waterflood: 0.05 } },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0002, fire: 0.0006, cold: 0.002 },
  },

  // ======================================================================== Tengiz, Kazakhstan (JV from 1993)
  {
    id: 'tengiz', name: 'Tengiz, Pre-Caspian Basin', body: 'earth', region: 'kazakhstan', regionId: 'kazakhstan',
    era: 'offshore', opensDay: D(1993, 4, 6), discoveredYear: 1979, grid: { w: 6, h: 4 }, waterDepthFt: 0,
    lat: 46.15, lon: 53.38, startPlot: 9, recommendedRig: 'rotary',
    leaseBase: 20e6, leaseYear: 1993, royalty: 0.2,
    concession: { kind: 'jv', stateCompany: 'Tengizchevroil (Chevron & Kazakhstan)', note: 'A 40-year JV signed 6 April 1993. The oil is extraordinary; so are the pressure, the H₂S and the export bottleneck.' },
    boomCurve: [[1993.25, 1], [1995, 1.5], [1998.6, 0.8]],
    environment: { kind: 'steppe', coldC: -35, heatC: 45, winterStorms: { months: [12, 1, 2], perYear: 3, hsFt: [0, 0], blizzard: true } },
    transport: ['rail@1993', 'trunk_pipeline@1993'],
    blurb: 'A Carboniferous reef three miles down, under a salt sheet, at 12,000 psi and 13% hydrogen sulphide. Well 37 blew out in 1985 and burned for a year.',
    strata: stack([
      [200,   'sand',      'Steppe sand & loam',                1, 8.5, 3],
      [3000,  'clay',      'Neogene & Paleogene clays',         2, 8.6, 10],
      [7500,  'shale',     'Cretaceous & Jurassic shale',       3, 8.8, 30],
      [12500, 'salt',      'Kungurian salt',                    3, 8.8, 15, { salt: true }],
      [18000, 'limestone', 'Carboniferous–Devonian reef platform', 6, 8.8, 200, { frac: 18.5, h2s: 120000 }],
    ]),
    prospects: [
      { cx: 2.5, cy: 1.5, rx: 2.4, ry: 1.6, reservoirs: [
        { id: 'te_platform', name: 'Tengiz carbonate platform', top: 12700, dipFtPerPlot: 60, thick: 2000, ooip: 1e10, qiRange: [3000, 15000], pore: 15.8, gusherChance: 0.1,
          ...RES, rf: 0.3, di: 0.06, api: 47, sour: true, h2s: 134000, eorPotential: { gas: 0.1 } },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0002, fire: 0.0008, cold: 0.001, heat: 0.0004 },
  },

  // ======================================================================== Deepwater Gulf of Mexico (Bullwinkle, Auger, Mars)
  {
    id: 'deep_gulf', name: 'Deepwater Gulf (Green Canyon–Garden Banks)', body: 'earth', region: 'gulf_of_mexico', regionId: 'gulf_of_mexico',
    era: 'offshore', opensDay: D(1983, 5, 25), discoveryDay: D(1983, 11, 1), grid: { w: 8, h: 4 },
    waterDepthFt: 1000, waterDepthMaxFt: 3200, waterAxis: 'x', shoreBase: 'Fourchon, La.', distanceToShoreMi: 120,
    lat: 27.4, lon: -91.2, startPlot: 9, recommendedRig: 'rotary',
    leaseBase: 1.5e6, leaseYear: 1983, royalty: 0.125,
    concession: { kind: 'lease', bidRound: 'ocs_sale_72', note: 'Area-wide leasing from Central Gulf Sale 72 (25 May 1983). Deep Water Royalty Relief Act 1995 waives royalty on early volumes.' },
    boomCurve: [[1983.4, 1], [1984, 2], [1986.2, 0.5], [1990, 1], [1995.9, 3], [1998, 2.5]],
    environment: { ...GULF_ENV, kind: 'deepwater', currentKn: 3, shallowGas: true },
    transport: ['offshore_pipeline@1988'],
    blurb: 'Beyond the shelf edge the bottom falls away to half a mile, then a mile. Shell thinks the turbidite sands down there are the next Prudhoe Bay.',
    strata: stack([
      [1500,  'clay',      'Soft hemipelagic clay',             1, 8.6, 2, { frac: 9.8 }],
      [2000,  'sand',      'Shallow water-flow sand',           1, 9.4, 3, { water: true, shallowFlow: true, frac: 10.2 }],
      [8000,  'shale',     'Pleistocene & Pliocene shale',      3, 8.8, 25, { frac: 11.6 }],
      [10500, 'sand',      'Pliocene turbidite sands',          3, 8.8, 20, { frac: 12.4 }],
      [13000, 'shale',     'Geopressured Miocene shale',        3, 12.2, 20, { ...GEO(12.2), frac: 13.8 }],
      [16000, 'sand',      'Miocene sheet sands',               4, 8.8, 20, { frac: 14.4 }],
      [22000, 'salt',      'Allochthonous salt canopy',         3, 9.8, 20, { salt: true, frac: 15 }],
    ]),
    prospects: [
      { cx: 1.5, cy: 1.5, rx: 1.4, ry: 1.2, reservoirs: [
        { id: 'dg_bullwinkle', name: 'Pliocene J sands (Bullwinkle)', top: 8600, dipFtPerPlot: 80, thick: 250, ooip: 4e8, qiRange: [3000, 12000], pore: 10.4, gusherChance: 0.05,
          ...OFF, rf: 0.35, di: 0.3, api: 32 },
      ] },
      { cx: 5.5, cy: 2, rx: 2.0, ry: 1.4, reservoirs: [
        { id: 'dg_auger', name: 'Miocene S & R sands (Auger–Mars)', top: 13300, dipFtPerPlot: 100, thick: 300, ooip: 1.2e9, qiRange: [5000, 20000], pore: 12.8, gusherChance: 0.05,
          ...OFF, rf: 0.38, di: 0.25, api: 30, gasCap: true, eorPotential: { waterflood: 0.08 } },
      ] },
    ],
    seeps: [12],
    hazards: { lightning: 0.0002, fire: 0.0004, storm: 0.0015 },
  },

  // ======================================================================== Campos Basin, Brazil (deepwater, opened 1997-99)
  {
    id: 'campos', name: 'Campos Basin (Marlim), Brazil', body: 'earth', region: 'brazil', regionId: 'brazil',
    era: 'offshore', opensDay: D(1999, 6, 15), discoveredYear: 1985, grid: { w: 6, h: 4 },
    waterDepthFt: 2000, waterDepthMaxFt: 3500, waterAxis: 'x', shoreBase: 'Macaé', distanceToShoreMi: 70,
    lat: -22.4, lon: -40.0, startPlot: 9, recommendedRig: 'rotary',
    leaseBase: 4e6, leaseYear: 1999, royalty: 0.1,
    concession: { kind: 'concession', bidRound: 'anp_r1_1999', stateCompany: 'Petrobras', note: 'The 1997 Petroleum Law ends Petrobras’ monopoly. ANP Round 1 (June 1999) offers deepwater blocks to everyone.' },
    boomCurve: [[1999.4, 1], [1999.6, 2]],
    environment: { kind: 'deepwater', winterStorms: { months: [5, 6, 7, 8, 9], perYear: 4, hsFt: [10, 26] }, currentKn: 2 },
    transport: ['shuttle_tanker@1999'],
    blurb: 'Petrobras has pushed subsea trees and FPSOs to three thousand feet here, alone. Now the blocks next door are up for auction.',
    strata: stack([
      [4000,  'marl',      'Pliocene–Miocene marls',            2, 8.6, 15, { frac: 10.6 }],
      [5000,  'shale',     'Oligocene shale',                   3, 8.7, 25, { frac: 11.0 }],
      [6500,  'sand',      'Carapebus turbidite sands',         2, 8.8, 15, { frac: 11.4 }],
      [9000,  'limestone', 'Albian Macaé limestone',            6, 8.8, 200, { frac: 12.2 }],
      [12000, 'salt',      'Aptian salt',                       3, 9.6, 20, { salt: true, frac: 14 }],
      [16000, 'limestone', 'Pre-salt carbonates (unexplored)',  6, 9.8, 200, { frac: 15 }],
    ]),
    prospects: [
      { cx: 2.5, cy: 1.5, rx: 2.4, ry: 1.5, reservoirs: [
        { id: 'cp_marlim', name: 'Marlim turbidites', top: 5300, dipFtPerPlot: 50, thick: 300, ooip: 6e9, qiRange: [5000, 20000], pore: 9.6, gusherChance: 0.05,
          ...OFF, rf: 0.3, di: 0.15, api: 20, heavy: true, viscosityCp: 20, eorPotential: { waterflood: 0.1 } },
      ] },
    ],
    seeps: [],
    hazards: { lightning: 0.0002, fire: 0.0004, storm: 0.0008 },
  },
];

// ------------------------------------------------------------------ M3 additions to existing (M1/M2) fields
// Applied by enableM3Fields() (src/data/fields.js): only APPENDS transport entries dated >= 1946 and adds new keys,
// so nothing changes before 1946 (fieldTransports filters by year; genField only reads the first entry).
export const M3_FIELD_PATCHES = {
  kirkuk:          { transport: ['tanker@1952'], m3Note: 'IPC 50/50 deal 1952; Law 80 (1961) strips undeveloped acreage; nationalized 1 June 1972; Kirkuk–Ceyhan line 1977.' },
  masjed_soleyman: { transport: ['tanker@1950'], m3Note: 'Nationalized with the rest of Anglo-Iranian on 1 May 1951.' },
  maracaibo:       { transport: ['tanker@1950', 'supertanker_charter@1959'], m3Note: 'Fifty-fifty from 1948; nationalized 1 Jan 1976.' },
  dammam:          { transport: ['tanker@1950', 'tapline@1950'], m3Note: 'Aramco’s first field; the 50/50 agreement arrives 30 Dec 1950.' },
  bahrain:         { transport: ['tanker@1950'], m3Note: 'BAPCO taken over by the state 1974–80.' },
  turner_valley:   { prorationGroup: 'alberta', m3Note: 'The Alberta Petroleum and Natural Gas Conservation Board prorates the province from 1950.' },
  yates:           { m3Note: 'Waterflood and gas injection candidates once unitized.' },
  east_texas:      { m3Note: 'Salt-water disposal and pressure maintenance from the 1940s.' },
};

// ------------------------------------------------------------------ pure helpers
// Water depth (ft) at plot idx: linear across the grid along waterAxis, from waterDepthFt to waterDepthMaxFt.
export function plotWaterDepth(field, idx) {
  const f = field;
  const w0 = f?.waterDepthFt ?? 0;
  const w1 = f?.waterDepthMaxFt ?? w0;
  if (w1 === w0) return w0;
  const w = f.grid.w, h = f.grid.h;
  const x = idx % w, y = Math.floor(idx / w);
  const t = (f.waterAxis ?? 'x') === 'y' ? (h > 1 ? y / (h - 1) : 0) : (w > 1 ? x / (w - 1) : 0);
  return Math.round(w0 + (w1 - w0) * t);
}
export const fieldMaxWater = (f) => Math.max(f?.waterDepthFt ?? 0, f?.waterDepthMaxFt ?? 0);
export const isOffshoreField = (f) => fieldMaxWater(f) > 0;
// Storm/winter season active for a field in calendar month m (1-12).
export function seasonFor(field, month) {
  const env = field?.environment ?? {};
  return {
    storm: !!env.stormSeason?.months?.includes(month),
    winter: !!env.winterStorms?.months?.includes(month),
    ice: !!env.ice?.months?.includes(month),
    iceRoads: !!env.ice?.drillSeason?.includes(month),
  };
}
